<script setup lang="ts">
import { useIdeStore } from '@/stores/ide.store'

import { IDEVIEWTABS, TAB_BUTTON_POSITION, TAB_ICONS_NAMES } from '@/utils/tabs'

import consoleIcon from '@/assets/images/ide/consoleIcon.svg'
import TabCompTitleBar from '@/components/ide/ide/TabCompTitleBar.vue'
import { computed, nextTick, onBeforeUnmount, onMounted, watch, ref } from 'vue'
import { Terminal } from '@xterm/xterm'
import { FitAddon } from 'xterm-addon-fit'
import { useAdvanceCompilerStore } from '@/stores/advanceCompiler.store'
// import { CURRENT_RUNNING_WS_SERVICE } from '@/components/organisation/enum/advanceCompiler.enums'
// import { ConsoleMsg } from '@/components/organisation/interface/IAdvanceCompiler'

const props = defineProps({
  tabPosition: {
    type: String,
    required: false,
    default: TAB_BUTTON_POSITION.MAXIMIZE
  }
})

const ideStore = useIdeStore()
const advCompilerStore = useAdvanceCompilerStore()
const resizeObserver = ref<ResizeObserver | null>(null)
const width = ref<number>(0)
const isDark = computed(() => ideStore?.colorMode === 'dark')

const console = computed(() => {
  return advCompilerStore.console
})

const consoleTheme = computed(() => {
  return {
    background: !isDark.value ? '#ffffff' : '#192330',
    foreground: !isDark.value ? '#000000' : '#ffffff',
    cursor: '#FD5200',
    selectionBackground: !isDark.value ? '#d3d3d3' : '#6c6d6c'
  }
})

const isPodInitialized = computed(() => {
  return advCompilerStore.projectIdAdvCompiler && advCompilerStore.isPodInitializationCompleted
})

const splitIdeTabPosition = computed(() => {
  if (props.tabPosition === TAB_BUTTON_POSITION.RIGHT) {
    return 'splitIdeTabRight'
  }
  if (props.tabPosition === TAB_BUTTON_POSITION.BOTTOM) {
    return 'splitTabButtomBar'
  }
  return `${IDEVIEWTABS.CONSOLE}-main`
})

/**
 * Displays console output, including logs, errors, and messages during code execution.
 */
const openConsole = () => {
  nextTick(() => {
    const consoleWrapper = document.getElementById(`${IDEVIEWTABS.CONSOLE}-main`)
    if (consoleWrapper) {
      console.value.xterm?.open(consoleWrapper)
      console.value.xterm?.focus()
      resizeActiveConsole()
    }
  })
}

/**
 * Resize active console using index and fitAddon
 */
const resizeActiveConsole = () => {
  const element = document.getElementById(splitIdeTabPosition.value)
  width.value = element?.offsetWidth ?? 0
  console.value.fitAddon?.fit()
}

/**
 * @returns new console & fitAddon instances
 */
const createNewConsole = () => {
  const newConsole = new Terminal({
    convertEol: true,
    cursorBlink: false,
    theme: consoleTheme.value
  })
  const fitAddon = new FitAddon()
  newConsole.loadAddon(fitAddon)

  newConsole.attachCustomKeyEventHandler((e: KeyboardEvent) => {
    // Ctrl + C
    if (e.ctrlKey && e.code === 'KeyC' && e.type === 'keydown') {
      const selection = newConsole.getSelection()
      if (selection) {
        navigator.clipboard.writeText(selection)
        return false
      }
    }

    // ESC
    if (e.code === 'Escape' && e.type === 'keydown') {
      newConsole.blur()
      return false
    }

    return true
  })

  return { newConsole, fitAddon }
}

/**
 * Creates a new console instance and sets up the WebSocket connection.
 */
const createConsole = () => {
  const { newConsole, fitAddon } = createNewConsole()

  advCompilerStore.setConsoleXtermAndFitAddonInstance(newConsole, fitAddon)

  nextTick(() => {
    openConsole()
    console.value.history?.forEach((line) => {
      newConsole.write(line)
    })
  })
}

/**
 * Initializes a new console , fitAddon and updates the store
 */
const reCreateConsole = () => {
  nextTick(() => {
    console.value.xterm?.dispose()
    const { newConsole, fitAddon } = createNewConsole()
    advCompilerStore.setConsoleXtermAndFitAddonInstance(newConsole, fitAddon)
    console.value.history?.forEach((line) => {
      newConsole.write(line)
    })
  })
}

watch(
  () => isPodInitialized.value,
  (init) => {
    if (init && console.value.xterm == null) {
      createConsole()
    }
  },
  {
    immediate: true
  }
)

onMounted(() => {
  resizeObserver.value = new ResizeObserver(resizeActiveConsole)
  const element = document.getElementById(splitIdeTabPosition.value)
  resizeObserver.value?.observe(element as HTMLElement)

  nextTick(() => {
    if (console.value.xterm) {
      openConsole()
    }
  })
})

onBeforeUnmount(() => {
  const element = document.getElementById(splitIdeTabPosition.value)
  resizeObserver.value?.unobserve(element as HTMLElement)
  nextTick(() => {
    reCreateConsole()
  })
})

watch(isDark, () => {
  if (console.value.xterm) {
    console.value.xterm.options.theme = consoleTheme.value
  }
})
</script>

<template>
  <div class="relative flex h-full w-full flex-col">
    <TabCompTitleBar
      v-if="ideStore.currentMobileTab == null && props.tabPosition == TAB_BUTTON_POSITION.MAXIMIZE"
      titleName="Console"
      :icon="consoleIcon"
      :tabPosition="props.tabPosition"
      :tabName="TAB_ICONS_NAMES.CONSOLE"
    />

    <div
      class="background-quaternary console-wrapper relative flex h-full w-full flex-col overflow-hidden"
      :id="`${IDEVIEWTABS.CONSOLE}-main`"
      :style="
        props.tabPosition !== TAB_BUTTON_POSITION.MAXIMIZE &&
        `width: calc(${width}px - ${
          props.tabPosition === TAB_BUTTON_POSITION.RIGHT ? '2px' : '0px'
        })`
      "
    ></div>
  </div>
</template>

<style>
.console-wrapper .terminal {
  height: 100%;
}
</style>
